import React from 'react';
import { Box, Chip, TextField, Tooltip, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import LockIcon from '@mui/icons-material/Lock';
import { TraitDefinition } from '../../interfaces/CourtInterfaces';

/**
 * Lloyd's ratings (6 Oct 2026): "make it such that we can click each one that is applicable".
 *
 * Shared by the court page and the prosecutor dialog. Each trait is a chip that is filled when on.
 * Negative and positive traits are shown apart, each with its own "Other" box.
 *
 * `detected` traits are read by the server from elsewhere on the court record (fax filing, a granted
 * remote appearance). They show as on and locked, with where they came from, because unticking one
 * here would not change the answer that produced it.
 */
interface TraitPickerProps {
  definitions: TraitDefinition[];
  selected: string[];
  onChange?: (selected: string[]) => void;
  detected?: { key: string; from: string }[];
  otherNegative: string;
  otherPositive: string;
  onOtherChange?: (side: 'negative' | 'positive', value: string) => void;
  /** Read-only: shows only what is on, and no "Other" boxes unless they hold something. */
  readOnly?: boolean;
  disabled?: boolean;
}

const SIDES: { polarity: 'negative' | 'positive'; title: string; color: 'warning' | 'success' }[] = [
  { polarity: 'negative', title: 'Makes it harder', color: 'warning' },
  { polarity: 'positive', title: 'Works in our favour', color: 'success' },
];

const TraitPicker: React.FC<TraitPickerProps> = ({
  definitions,
  selected,
  onChange,
  detected = [],
  otherNegative,
  otherPositive,
  onOtherChange,
  readOnly = false,
  disabled = false,
}) => {
  const detectedFrom = new Map(detected.map((d) => [d.key, d.from]));
  const on = new Set(selected);

  const toggle = (key: string) => {
    if (!onChange) return;
    onChange(on.has(key) ? selected.filter((k) => k !== key) : [...selected, key]);
  };

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {SIDES.map(({ polarity, title, color }) => {
        const traits = definitions.filter((d) => d.polarity === polarity);
        const other = polarity === 'negative' ? otherNegative : otherPositive;
        const visible = readOnly
          ? traits.filter((t) => on.has(t.key) || detectedFrom.has(t.key))
          : traits;

        return (
          <Box key={polarity}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {title}
            </Typography>

            {readOnly && visible.length === 0 && !other ? (
              <Typography variant="body2" color="text.disabled">
                None recorded
              </Typography>
            ) : (
              <Box display="flex" flexWrap="wrap" gap={1}>
                {visible.map((trait) => {
                  const from = detectedFrom.get(trait.key);
                  const active = !!from || on.has(trait.key);
                  const chip = (
                    <Chip
                      key={trait.key}
                      label={trait.label}
                      size="small"
                      color={active ? color : 'default'}
                      variant={active ? 'filled' : 'outlined'}
                      icon={from ? <LockIcon /> : active && !readOnly ? <CheckIcon /> : undefined}
                      onClick={readOnly || from || disabled ? undefined : () => toggle(trait.key)}
                    />
                  );
                  return from ? (
                    <Tooltip key={trait.key} title={`Set automatically. ${from}`}>
                      <span>{chip}</span>
                    </Tooltip>
                  ) : (
                    chip
                  );
                })}
              </Box>
            )}

            {readOnly ? (
              other && (
                <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                  Other: {other}
                </Typography>
              )
            ) : (
              <TextField
                label="Other"
                size="small"
                fullWidth
                multiline
                maxRows={3}
                sx={{ mt: 1.5 }}
                value={other}
                onChange={(e) => onOtherChange?.(polarity, e.target.value)}
                disabled={disabled}
                inputProps={{ maxLength: 500 }}
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default TraitPicker;
